import type {AutoMergeRequest} from '../../../../types'

export type MergeControlType =
  | 'disable-auto-merge'
  | 'inactive-direct'
  | 'merge-queue-bypass'
  | 'merge-queue'
  | 'auto-merge'
  | 'direct'

export type MergeControlModel = {type: MergeControlType; visibility: 'hidden' | 'visible'}

export type DeriveMergeControlModelInput = {
  autoMergeRequest: AutoMergeRequest
  hasStackAutoMergeBlocker?: boolean
  isAdminBypassToggleChecked: boolean
  isAsyncStackMerge: boolean
  isBypassAllowed: boolean
  isDirectMergeEnabled: boolean
  isMergeQueueEnabled: boolean
  isPullRequestStacksAutoMergeEnabled?: boolean
  isStackAutoMergePermissionEnabled?: boolean
  isStackMergeBlocked?: boolean
  isStackMergeInProgress: boolean
  isStacked?: boolean
  viewerCanEnableAutoMerge: boolean
  viewerCanEnableStackAutoMerge?: boolean
}

export function deriveMergeControlModel({
  autoMergeRequest,
  hasStackAutoMergeBlocker = false,
  isAdminBypassToggleChecked,
  isAsyncStackMerge,
  isBypassAllowed,
  isDirectMergeEnabled,
  isMergeQueueEnabled,
  isPullRequestStacksAutoMergeEnabled = false,
  isStackAutoMergePermissionEnabled = false,
  isStackMergeBlocked = false,
  isStackMergeInProgress,
  isStacked,
  viewerCanEnableAutoMerge,
  viewerCanEnableStackAutoMerge = false,
}: DeriveMergeControlModelInput): MergeControlModel {
  // The server answers only whether this user is allowed to enable stacked auto-merge here. Stacked auto-merge then
  // waits for the whole stack, so we offer it only while the stack is blocked and nothing in it is a draft, closed,
  // conflicting, or needs a rebase. Without the permission flag we keep the old unstacked answer.
  const canEnableAutoMerge =
    isStacked && isStackAutoMergePermissionEnabled
      ? viewerCanEnableStackAutoMerge && isStackMergeBlocked && !hasStackAutoMergeBlocker
      : viewerCanEnableAutoMerge

  let type: MergeControlType

  switch (true) {
    // Auto-merge is active because a request is present.
    case Boolean(autoMergeRequest):
      type = 'disable-auto-merge'
      break
    // Neither merge path is currently available.
    case !isMergeQueueEnabled && !isDirectMergeEnabled:
      type = 'inactive-direct'
      break
    // Merge queue is enabled, but the user can bypass merge queue and did bypass merge queue, so we let them direct merge
    case isMergeQueueEnabled && isBypassAllowed && isAdminBypassToggleChecked:
      type = 'merge-queue-bypass'
      break
    // A blocked stack cannot be enqueued yet, but stacked auto-merge can wait for every entry to become ready.
    case isMergeQueueEnabled &&
      isStackMergeBlocked &&
      canEnableAutoMerge &&
      isStacked &&
      isPullRequestStacksAutoMergeEnabled:
      type = 'auto-merge'
      break
    // Merge queue is enabled but the user hasn't elected to bypass it (or possibly cannot bypass it)
    case isMergeQueueEnabled:
      type = 'merge-queue'
      break
    // Auto merge is allowed and the user hasn't bypassed it to direct merge.
    // Stacked PRs only support auto-merge behind pullRequestStacksAutoMerge; otherwise they fall through to the disabled merge button below.
    case canEnableAutoMerge && !isAdminBypassToggleChecked && (!isStacked || isPullRequestStacksAutoMergeEnabled):
      type = 'auto-merge'
      break
    default:
      type = 'direct'
  }

  const visibility = isStackMergeInProgress && isAsyncStackMerge ? 'hidden' : 'visible'
  return {visibility, type}
}
